import { useCallback } from 'react'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { LiveLayer, transformReturn } from '../Live.types'
import mapboxgl from 'mapbox-gl'
import { BASE_API_URL } from '@app/app-constants'
import * as turf from '@turf/turf'
import { OrionOutagesResponse } from '../feedtypes/orion-power-outages'
export const useTransformLiveLayers = () => {
  const { mapLayerStyles } = useMap()

  const transformLiveFeedToGeoJSON = useCallback(
    async (liveLayer: LiveLayer, liveFeedResponse: Response): Promise<transformReturn> => {
      if (liveLayer.feedType === 'geojson') {
        const response = (await liveFeedResponse.json()) as transformReturn
        response.features = response.features ?? []

        // Filter out any features older than 24h if `calc_age` is set.
        const propertyToCalcAgeWith = liveLayer.feature_properties?.find(
          (property) => property.calc_age,
        )?.property

        if (propertyToCalcAgeWith) {
          const dayInMs = 24 * 60 * 60 * 1000
          const now = Date.now().valueOf()

          response.features = response.features.filter(
            (feature) =>
              feature.properties &&
              now - new Date(feature.properties[propertyToCalcAgeWith]).valueOf() < dayInMs,
          )
        }

        // Filter out any features if `filter_out` is defined for a property
        const propertiesToFilterBy =
          liveLayer.feature_properties
            ?.filter((property) => property.filter_out)
            .map((feature) => feature.property) ?? []

        propertiesToFilterBy.forEach((propertyToFilterBy) => {
          const valuesToFilterOut = liveLayer.feature_properties?.find(
            (fp) => fp.property == propertyToFilterBy,
          )?.filter_out

          if (!valuesToFilterOut) return

          response.features = response.features.filter(
            (feature) =>
              feature.properties &&
              !valuesToFilterOut.includes(feature.properties[propertyToFilterBy]),
          )
        })

        return response
      }

      if (liveLayer.feedType === 'cap_atom') {
        const toReturn: transformReturn = {
          features: [],
          type: 'FeatureCollection',
          icon: '',
          color: '',
          lastUpdated: undefined,
        }

        const alertLookup: { [key: string]: Document | null } = {}

        const parser = new DOMParser()
        const xml = await liveFeedResponse.text()
        //const xml = DUMMY_MET_SERVICE_FEED
        const xmlDoc = parser.parseFromString(xml, 'application/xml')

        const entries = Array.from(xmlDoc.querySelectorAll('entry'))

        // if entries.length == 0, then there are no alerts, so return an empty geojson
        if (entries.length === 0) return toReturn

        // Load all alerts into alertLookup so Cancel and Update types can be processed
        await Promise.all(
          entries.map(async (entry) => {
            const ogCapLink = entry.querySelector('link')?.getAttribute('href')

            let capLink = ogCapLink

            if (liveLayer.proxy_url) {
              capLink = capLink?.replace(
                liveLayer.proxy_url,
                `${BASE_API_URL}/layer/live_feeds/${liveLayer.id}/proxy/`,
              )
            }

            if (capLink) {
              const alertResponse = await fetch(capLink, { credentials: 'include' })
              const alertXml = await alertResponse.text()
              const alertXmlDoc = parser.parseFromString(alertXml, 'application/xml')

              // only alerts with a status of “Actual” should be actioned by recipients. All other settings of
              // status (such as “Test”, “Exercise”, “System” or “Draft”) should be ignored and/or discarded.
              const status = alertXmlDoc.querySelector('alert > status')?.textContent
              if (status !== 'Actual') return

              const sender = alertXmlDoc.querySelector('alert > sender')?.textContent
              const identifier = alertXmlDoc.querySelector('alert > identifier')?.textContent
              const sent = alertXmlDoc.querySelector('alert > sent')?.textContent
              const alertKey = `${sender},${identifier},${sent}`
              alertLookup[alertKey] = alertXmlDoc
            }
          }),
        )

        // Delete errored or cancelled alerts and alerts that have been updated
        // (Setting them to null as we're looping over the entries as we do them)
        for (const [alertKey, alertXmlDoc] of Object.entries(alertLookup)) {
          if (!alertXmlDoc) continue

          const msgType = alertXmlDoc.querySelector('alert > msgType')?.textContent

          if (msgType === 'Cancel' || msgType === 'Error') {
            const alertKeyToDelete = alertXmlDoc.querySelector('alert > references')?.textContent

            // Delete both the cancel/error alert and the one it references
            if (alertKeyToDelete) alertLookup[alertKeyToDelete] = null
            alertLookup[alertKey] = null
          }

          if (msgType === 'Update') {
            const alertKeyToUpdate = alertXmlDoc.querySelector('alert > references')?.textContent
            if (alertKeyToUpdate) alertLookup[alertKeyToUpdate] = null
          }

          // “Ack” - Acknowledges receipt and acceptance of the message(s) identified in <references>
          // Not used by us so simply delete them
          if (msgType === 'Ack') {
            alertLookup[alertKey] = null
          }
        }

        // Now generate geojson for the remaining alerts
        for (const [alertKey, alertXmlDoc] of Object.entries(alertLookup)) {
          if (!alertXmlDoc) continue

          const expires = alertXmlDoc.querySelector('alert > info > expires')?.textContent
          if (expires && new Date(expires).valueOf() < Date.now().valueOf()) continue

          // The CAP format allows polygons to be optional but the metservice states they
          // will always have at least one
          const polygons = alertXmlDoc.querySelectorAll('alert > info > area > polygon')
          if (polygons.length === 0) continue

          const multiPolygonCoordinates = Array.from(polygons).map((polygon) => {
            const coordinates = (polygon.textContent ?? '').trim().split(' ')
            return coordinates
              .map((coordinateString) => coordinateString.split(','))
              .map(
                (coordinate) =>
                  [
                    Number.parseFloat(coordinate[1]),
                    Number.parseFloat(coordinate[0]),
                  ] as GeoJSON.Position,
              )
          })

          const alertElement = alertXmlDoc.querySelector('alert')
          const alertProperties: Record<string, string> = {}
          Array.from(alertElement?.children ?? []).forEach((child) => {
            if (child.children.length === 0)
              alertProperties[child.localName] = child.textContent ?? ''
          })

          if (alertProperties.sent) {
            const sentDate = new Date(alertProperties.sent)
            if (!toReturn.lastUpdated || sentDate > new Date(toReturn.lastUpdated)) {
              toReturn.lastUpdated = alertProperties.sent
            }
          }

          // Parse the rest of the alert xml and add each value to the geojson properties.
          const info = alertXmlDoc.querySelector('alert > info')
          const infoProperties: Record<string, string> = {}
          const paramProperties: Record<string, string> = {}
          let areaDesc: string | null = null

          Array.from(info?.children ?? []).forEach((child) => {
            if (child.localName === 'parameter') {
              const valueName = child.querySelector('valueName')?.textContent
              if (valueName)
                paramProperties[valueName] = child.querySelector('value')?.textContent ?? ''
            } else if (child.localName === 'area') {
              areaDesc = child.querySelector('areaDesc')?.textContent ?? null
            } else if (child.children.length === 0) {
              infoProperties[child.localName] = child.textContent ?? ''
            }
          })

          const allProps = { ...alertProperties, ...infoProperties, ...paramProperties }

          const legend = liveLayer.live_legend
          const legendColor = legend?.key
            ? legend.items.find((item) => item.label === allProps[legend.key!])?.color
            : undefined

          const iconsConfig = liveLayer.icons
          const iconValue = iconsConfig?.icon_property ? allProps[iconsConfig.icon_property] : ''
          const resolvedIcon = iconsConfig?.icons.includes(iconValue) ? iconValue : ''

          toReturn.features.push({
            type: 'Feature',
            geometry: {
              type: 'MultiPolygon',
              coordinates: multiPolygonCoordinates.map((coordinates) => [coordinates]),
            },
            properties: {
              ...alertProperties,
              ...infoProperties,
              areaDesc,
              ...paramProperties,
              icon: resolvedIcon,
              color: legendColor ?? paramProperties.ColourCodeHex ?? '',
            },
          })
          try {
            multiPolygonCoordinates.forEach((polygonCoordinates) => {
              const centrePoint = turf.pointOnFeature(turf.polygon([polygonCoordinates]))

              centrePoint.properties = {
                ...alertProperties,
                ...infoProperties,
                areaDesc,
                ...paramProperties,
              }
              toReturn.features.push(centrePoint)
            })
          } catch (e) {
            console.warn((e as Error).message, alertKey)
          }
        }

        const layerOrder = ['Yellow', 'Orange', 'Red']

        toReturn.features = toReturn.features.sort((a, b) => {
          if (!a.properties || !b.properties) return 0
          return (
            layerOrder.indexOf(a.properties['ColourCode']) -
            layerOrder.indexOf(b.properties['ColourCode'])
          )
        })

        return toReturn
      }

      if (liveLayer.feedType === 'orion_power_outages') {
        const toReturn: transformReturn = {
          features: [],
          type: 'FeatureCollection',
          icon: '',
          color: '',
          lastUpdated: undefined,
        }

        const response = (await liveFeedResponse.json()) as OrionOutagesResponse

        response.CurrentOutages.forEach((outage) => {
          toReturn.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [outage.Longitude, outage.Latitude],
            },
            properties: {
              ...outage,
            },
          })

          if (outage.Centroid && outage.Centroid.Radius > 0) {
            const circle = turf.circle(
              [outage.Centroid.Longitude, outage.Centroid.Latitude],
              outage.Centroid.Radius,
              { units: 'meters' },
            )
            circle.properties = { ...outage }
            toReturn.features.push(circle)
          }
        })

        return toReturn
      }

      console.warn(`No matching live feed transformer found for feed type: "${liveLayer.feedType}"`)
      return { features: [], type: 'FeatureCollection', color: '', icon: '' }
    },
    [],
  )

  const transformLiveLayerToMapboxLayers = useCallback(
    (liveLayer: LiveLayer): mapboxgl.AnyLayer[] => {
      const toReturn: mapboxgl.AnyLayer[] = []

      const layerTypes = ['circle', 'fill', 'line', 'symbol', 'text']

      layerTypes.forEach((layerType) => {
        if (!liveLayer.styles || !liveLayer.styles[layerType]) return

        let mapLayerStyle = { paint: {}, layout: {} }

        // Allow live styles to merge multiple styles - saves on duplicates
        if (Array.isArray(liveLayer.styles[layerType])) {
          const mapLayerStyleIds = liveLayer.styles[layerType]
          mapLayerStyleIds.forEach((styleId) => {
            const style = mapLayerStyles[styleId]
            mapLayerStyle.layout = { ...mapLayerStyle.layout, ...style.layout }
            mapLayerStyle.paint = { ...mapLayerStyle.paint, ...style.paint }
          })
        } else {
          const mapLayerStyleId = liveLayer.styles[layerType]
          if (mapLayerStyles[mapLayerStyleId]) mapLayerStyle = mapLayerStyles[mapLayerStyleId]
        }

        switch (layerType) {
          case 'line':
            toReturn.push({
              id: `live-line-layer-${liveLayer.id}`,
              source: `live-feed-${liveLayer.id}`,
              type: 'line',
              layout: mapLayerStyle.layout,
              paint: mapLayerStyle.paint,
            })
            break

          case 'circle':
            toReturn.push({
              id: `live-circle-layer-${liveLayer.id}`,
              source: `live-feed-${liveLayer.id}`,
              type: 'circle',
              filter: ['==', ['geometry-type'], 'Point'],
              layout: mapLayerStyle.layout,
              paint: mapLayerStyle.paint,
            })
            break

          case 'fill':
            toReturn.push({
              id: `live-fill-layer-${liveLayer.id}`,
              source: `live-feed-${liveLayer.id}`,
              type: 'fill',
              layout: mapLayerStyle.layout,
              paint: mapLayerStyle.paint,
            })
            break

          case 'symbol': {
            if (!liveLayer.icons) break
            toReturn.push({
              id: `live-symbol-layer-${liveLayer.id}`,
              source: `live-feed-${liveLayer.id}`,
              type: 'symbol',
              filter: ['==', ['geometry-type'], 'Point'],
              layout: {
                'icon-image': [
                  'concat',
                  liveLayer.id,
                  '-',
                  [
                    'concat',
                    // Creates for example: metservice_weather_warnings-rain-Orange
                    ...liveLayer.icons.icon_properties.map(
                      (iconProperty, index, array) =>
                        index < array.length - 1
                          ? ['concat', ['get', iconProperty], '-']
                          : ['get', iconProperty], // This automatically removes spaces - neat!
                    ),
                  ],
                ],
                'icon-allow-overlap': true,
                ...mapLayerStyle.layout,
              },
              paint: mapLayerStyle.paint,
            })
            break
          }

          case 'text': {
            toReturn.push({
              id: `live-text-layer-${liveLayer.id}`,
              source: `live-feed-${liveLayer.id}`,
              type: 'symbol', // Yes "symbol" for showing text
              filter: ['==', ['geometry-type'], 'Point'],
              layout: {
                'text-font': ['Inter Regular'],
                'text-size': 10,
                ...mapLayerStyle.layout,
              },
              paint: mapLayerStyle.paint,
            })
            break
          }
        }
      })

      // If `show_circle` is defined for a property then add a circle
      // layer which shows when zoomed out
      const zoomProperty = liveLayer.feature_properties?.find((fp) => fp.show_circle)
      if (zoomProperty?.show_circle) {
        const zoomLevel = zoomProperty.show_circle.zoom_less_than
        const zoomValue = zoomProperty.show_circle.value_less_than
        const styleId = liveLayer.styles
          ? (liveLayer.styles['line'] ?? liveLayer.styles['fill'])
          : null

        if (styleId && !Array.isArray(styleId)) {
          const paintStyle = mapLayerStyles[styleId].paint
          const circleColor = paintStyle['line-color'] ?? paintStyle['fill-color']

          toReturn.push({
            id: `live-zoomed-circle-${liveLayer.id}`,
            source: `live-feed-${liveLayer.id}`,
            type: 'circle',
            paint: {
              'circle-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                zoomLevel,
                zoomValue ? ['case', ['<', ['get', zoomProperty.property], zoomValue], 1, 0] : 1,
                zoomLevel + 0.5,
                0,
              ],
              'circle-color': circleColor,
            },
            maxzoom: zoomLevel + 1,
          })
        }
      }

      return toReturn
    },
    [mapLayerStyles],
  )

  return { transformLiveFeedToGeoJSON, transformLiveLayerToMapboxLayers }
}

const DUMMY_MET_SERVICE_FEED = `<?xml version="1.0" encoding="utf-8"?>
<feed xmlns="http://www.w3.org/2005/Atom">
  <id>https://alerts.metservice.com/cap/atom</id>
  <link href="https://metservice.com"/>
  <updated>2026-05-06T09:08:17+12:00</updated>
  <title>Current Watches, Warnings and Advisories for New Zealand issued by MetService</title>
  <rights>Copyright Meteorological Service of New Zealand Limited, 2026. Licensed under Creative Commons BY 4.0.</rights>
  <entry>
    <title>Heavy Rain Watch</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.3</id>
    <category term="Met" />
    <summary>Periods of heavy rain, and amounts may exceed warning criteria. Impact: Streams and rivers may rise rapidly. Surface flooding, slips, and difficult driving conditions possible. Chance of upgrading to a Warning: High.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.3"/>
  </entry>
  <entry>
    <title>Heavy Rain Warning - Orange</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.7</id>
    <category term="Met" />
    <summary>Expect 250 to 350 mm of rain, but possibly as much as 400 mm about the ranges. Peaks rates of 20 to 30 mm/h expected during Friday with thunderstorms possible. Impact: Streams and rivers may rise rapidly. Surface flooding, slips, and difficult driving conditions possible. Chance of upgrading to a Red Warning: Low.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.7"/>
  </entry>
  <entry>
    <title>Heavy Rain Warning - Orange</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.6</id>
    <category term="Met" />
    <summary>Expect 200 to 300 mm of rain about the ranges, and lesser amounts near the coast. Peak rates of 15 to 25 mm/h expected during Friday with thunderstorms possible. Impact: Streams and rivers may rise rapidly. Surface flooding, slips, and difficult driving conditions possible. Chance of upgrading to a Red Warning: Low.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.6"/>
  </entry>
  <entry>
    <title>Heavy Rain Watch</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.2</id>
    <category term="Met" />
    <summary>Periods of heavy rain. Rainfall amounts may approach warning criteria, especially about the ranges. Note, another period of heavy rain is expected from late Friday morning. Chance of upgrading to a Warning: Moderate.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.2"/>
  </entry>
  <entry>
    <title>Heavy Rain Watch</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.1</id>
    <category term="Met" />
    <summary>Periods of heavy rain, and rainfall amounts may approach warning criteria within 15 km east of the main divide. Thunderstorms possible Thursday afternoon. Freezing level about 3000 metres. Note, another period of heavy rain is expected from Friday afternoon. Chance of upgrading to a Warning: Moderate.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.1"/>
  </entry>
  <entry>
    <title>Heavy Rain Watch</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.0</id>
    <category term="Met" />
    <summary>Periods of heavy rain. Rainfall amounts may approach warning criteria within 15 km east of the main divide. Freezing level about 3000 metres.  Note, another period of heavy rain is expected from Friday afternoon. Chance of upgrading to a Warning: Moderate.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.0"/>
  </entry>
  <entry>
    <title>Heavy Rain Warning - Orange</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.5</id>
    <category term="Met" />
    <summary>Expect 200 to 300 mm of rain about the ranges, and lesser amounts near the coast. The largest accumulations are likely about and south of the glaciers, where some models indicate 350 mm is possible about the ranges. Peak rates of 25 to 35 mm/h expected during Thursday with thunderstorms possible.  Note, another period of heavy rain is expected from Friday afternoon. Impact: Streams and rivers may rise rapidly. Surface flooding, slips, and difficult driving conditions possible. Chance of upgrading to a Red Warning: Low. Changes: The chance of upgrade has been increased Low.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.5"/>
  </entry>
  <entry>
    <title>Heavy Rain Warning - Orange</title>
    <id>2.49.0.0.554.0.severeweather.nz.20260505210817355.4</id>
    <category term="Met" />
    <summary>Expect 140 to 180 mm of rain, especially north of George Sound. Peak rates of 15 to 25 mm/h expected Thursday morning with thunderstorms possible. Note, another period of heavy rain is expected from Friday afternoon. Impact: Streams and rivers may rise rapidly. Surface flooding, slips, and difficult driving conditions possible. Chance of upgrading to a Red Warning: Minimal. Changes: Watch upgraded to an Orange Warning.</summary>
    <updated>2026-05-06T09:08:17+12:00</updated>
    <published>2026-05-06T09:08:17+12:00</published>
    <author>
      <name>Meteorological Service of New Zealand Limited</name>
    </author>
    <link rel="related" type="application/cap+xml" href="https://alerts.metservice.com/cap/alert?id=2.49.0.0.554.0.severeweather.nz.20260505210817355.4"/>
  </entry>
</feed>`
